import { useState, useEffect, useCallback } from 'react';
import Box        from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon  from '@mui/icons-material/Close';

import { MATERIAL_TYPES } from './ShortlistShared3';
import {
  SourcePhotoLink, SourcePhotoPeek, RemovalNotice, useSourcePeek, useGlow,
} from './SourcePhoto';
import { apiFetch, friendlyMessage } from '../../../api/apiClient';
import { LoadingState, ErrorState, InlineAlert, useToast } from '../../../shared/components/status';

import {
  GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50,
  labelMono, bodyFont,
} from '../../../theme/tokens2';

// ─── Form field style ─────────────────────────────────────────────────────────
const inputSx = (focused) => ({
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${focused ? GREEN : BORDER}`,
  backgroundColor: '#fff',
  px: 2, py: '12px',
  fontSize: '0.875rem', color: CHARCOAL,
  ...bodyFont,
  outline: 'none',
  transition: 'border-color 0.2s ease',
  '&::placeholder': { color: 'rgba(0,0,0,0.25)' },
});

// ─── FormField ────────────────────────────────────────────────────────────────
function FormField({ label, htmlFor, children }) {
  return (
    <Box>
      <Typography component="label" htmlFor={htmlFor} sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, display: 'block', mb: 1 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// ─── MaterialRow ──────────────────────────────────────────────────────────────
// scenesData / onOpenSource: for the "See in photo" link. Leave onOpenSource
// out (after the request is submitted) and the link is hidden.
function MaterialRow({ item, onRemove, scenesData, onOpenSource, isGlowing }) {
  const name = item.namelabel || 'Untitled';
  const [rowRef, glowSx] = useGlow(isGlowing);
  return (
    <Box ref={rowRef} sx={{
      display: 'flex', alignItems: 'center', gap: '12px',
      px: '20px', py: '10px',
      backgroundColor: '#fff',
      borderBottom: `0.5px solid ${BORDER}`,
      scrollMarginTop: '12px', scrollMarginBottom: '12px',
      ...glowSx,
    }}>
      <Box
        component="img"
        src={item.imagesrc}
        alt=""
        sx={{
          width: 48, height: 48, objectFit: 'cover',
          flexShrink: 0, border: `0.5px solid ${BORDER}`,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '11px', fontWeight: 500, color: CHARCOAL,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          ...bodyFont,
        }}>
          {name}
        </Typography>

        {item.vendor && (
          <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED, mt: '2px' }}>
            {item.vendor}
          </Typography>
        )}

        {/* Opens the Moodboard photo this came from, on top of this page */}
        {onOpenSource && (
          <SourcePhotoLink
            item={item} scenesData={scenesData} showRoom
            onOpen={(it, scene) => onOpenSource(it, scene, it.cat_materials)}
          />
        )}
      </Box>

      {/* Remove from this request (hidden once the request is submitted) */}
      {onRemove && (
        <Box
          component="button"
          type="button"
          onClick={() => onRemove(item)}
          aria-label={`Remove ${name} from this request`}
          sx={{
            background: 'none', border: 'none', cursor: 'pointer',
            width: 28, height: 28, flexShrink: 0, color: MUTED_FAINT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { color: MUTED }, transition: 'color 0.15s',
          }}
        >
          <CloseIcon sx={{ fontSize: '0.875rem' }} />
        </Box>
      )}
    </Box>
  );
}

// ─── RequestSamples ───────────────────────────────────────────────────────────
// onBackToMaterials: passed from the Dashboard; opens the Materials page.
export default function RequestSamples({ onBackToMaterials }) {
  const toast = useToast();

  const [samplesItems, setSamplesItems] = useState([]);
  const [scenesData,   setScenesData]   = useState([]);
  const [status,       setStatus]       = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError,    setLoadError]    = useState('');

  // The address fields match the HouseholdProfile columns.
  const [address, setAddress] = useState({
    address_name:    '',
    address_unit:    '',
    address_street:  '',
    address_postal:  '',
    address_country: 'Singapore',
  });

  const [focusedField,  setFocusedField]  = useState(null);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitted,     setSubmitted]     = useState(false);
  const [submitError,   setSubmitError]   = useState('');

  // Materials marked for samples, plus any address already saved
  const loadAll = useCallback(async () => {
    setStatus('loading');
    try {
      const data = (await apiFetch('/api/MaterialsShortlist/my')) || [];
      setSamplesItems(data.filter((m) => m.in_samples_request));
      setStatus('ready');
    } catch (err) {
      setLoadError(friendlyMessage(err));
      setStatus('error');
      return;
    }
    // Fill in the address from last time, if there is one. Not essential.
    try {
      const profile = await apiFetch('/api/householdprofile/my');
      if (profile) {
        setAddress((prev) => ({
          address_name:    profile.address_name    || prev.address_name,
          address_unit:    profile.address_unit    || prev.address_unit,
          address_street:  profile.address_street  || prev.address_street,
          address_postal:  profile.address_postal  || prev.address_postal,
          address_country: 'Singapore',
        }));
      }
    } catch (_) { /* the customer can still type it in */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Photos are only used for the "See in photo" links, so a failure here
  // isn't worth an error message: the links simply don't show.
  useEffect(() => {
    apiFetch('/api/scenes/my').then((d) => setScenesData(d || [])).catch(() => {});
  }, []);

  // Reload just the list (not the address the customer may be typing),
  // after the "See in photo" pop-up closes with changes.
  const refreshSamples = useCallback(async () => {
    try {
      const data = (await apiFetch('/api/MaterialsShortlist/my')) || [];
      setSamplesItems(data.filter((m) => m.in_samples_request));
    } catch (err) {
      toast.error("We couldn't refresh your samples list. Please reload the page.");
    }
  }, [toast]);

  const sourcePeek = useSourcePeek(refreshSamples);

  // Remove one item from this request (it stays on the Materials shortlist).
  const handleRemove = async (item) => {
    setSamplesItems((prev) => prev.filter((m) => m.id !== item.id));
    try {
      await apiFetch(`/api/MaterialsShortlist/${item.id}/toggle-samples`, { method: 'PUT' });
    } catch (err) {
      // Put it back, so the list shows what is really in the request.
      setSamplesItems((prev) => [...prev, item]);
      toast.error(`We couldn't remove ${item.namelabel || 'this item'}. Please try again.`);
    }
  };

  const handleChange = (field) => (e) => {
    setAddress((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    const required = ['address_name', 'address_unit', 'address_street', 'address_postal'];
    if (required.some((f) => !address[f].trim())) {
      setSubmitError('Please fill in your name, unit, street and postal code.');
      return;
    }
    if (!/^\d{6}$/.test(address.address_postal.trim())) {
      setSubmitError('Please enter a 6-digit Singapore postal code.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      // Save the address to the customer's profile.
      await apiFetch('/api/householdprofile/address', { method: 'PATCH', body: address });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(friendlyMessage(err, "We couldn't save your address. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // Group by material type, in the shared order, plus "Other" at the end for
  // any type that isn't on the list. Nothing is ever hidden.
  const knownIds = MATERIAL_TYPES.map((t) => t.id);
  const groups = [
    ...MATERIAL_TYPES.map((t) => ({
      label: t.label,
      items: samplesItems.filter((i) => i.cat_materials === t.id),
    })),
    { label: 'Other', items: samplesItems.filter((i) => !knownIds.includes(i.cat_materials)) },
  ].filter((g) => g.items.length > 0);

  if (status === 'loading') {
    return <LoadingState label="Loading your samples request…" size="page" />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        size="page"
        title="We couldn't load your samples request"
        message={loadError}
        onRetry={loadAll}
      />
    );
  }

  const textFields = [
    { field: 'address_name',   label: 'NAME',          placeholder: 'Full name',           autoComplete: 'name' },
    { field: 'address_unit',   label: 'UNIT / BLOCK',  placeholder: '#08-12 / Blk 123',    autoComplete: 'address-line2' },
    { field: 'address_street', label: 'STREET',        placeholder: 'Street name',         autoComplete: 'address-line1' },
    { field: 'address_postal', label: 'POSTAL CODE',   placeholder: '6-digit postal code', autoComplete: 'postal-code', inputMode: 'numeric' },
  ];

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%', background: '#f7f7f5', overflow: 'hidden',
    }}>
      {/* ── Page header ── */}
      <Box sx={{
        background: '#fff', borderBottom: `0.5px solid ${BORDER}`,
        px: '20px', py: '14px', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <Typography component="h1" sx={{ ...labelMono, fontSize: '10px', color: CHARCOAL }}>
            Request Samples
          </Typography>
          <Typography aria-hidden sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT }}>·</Typography>
          <Typography sx={{ fontSize: '11px', color: MUTED, fontStyle: 'italic', ...bodyFont }}>
            Review your selection and confirm your delivery address.
          </Typography>
        </Box>
      </Box>

      {/* ── Two-column body ── */}
      <Box sx={{
        flex: 1, overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '5fr 4fr' },
      }}>

        {/* ── Left column: materials ── */}
        <Box sx={{ borderRight: { lg: `0.5px solid ${BORDER}` } }}>

          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: '20px', py: '8px',
            borderBottom: `0.5px solid ${BORDER}`,
            background: '#f7f7f5',
          }}>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>
              {samplesItems.length} sample{samplesItems.length !== 1 ? 's' : ''}
            </Typography>

            {onBackToMaterials && !submitted && (
              <Box
                component="button"
                type="button"
                onClick={onBackToMaterials}
                sx={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  ...labelMono, fontSize: '8px', color: GREEN,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Edit in Materials Shortlist ↗
              </Box>
            )}
          </Box>

          <RemovalNotice
            items={sourcePeek.notice}
            onDismiss={sourcePeek.dismissNotice}
            sx={{ mx: '20px', mt: '12px' }}
          />

          {/* Empty state */}
          {samplesItems.length === 0 && (
            <Box sx={{
              py: 8, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 2, px: '20px',
            }}>
              <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT }}>
                No samples selected yet
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: MUTED, textAlign: 'center', ...bodyFont }}>
                Go to Materials Shortlist and mark items with "+ Add to samples".
              </Typography>

              {onBackToMaterials && (
                <Box
                  component="button"
                  type="button"
                  onClick={onBackToMaterials}
                  sx={{
                    ...labelMono, fontSize: '9px', color: '#fff',
                    background: GREEN, border: 'none', cursor: 'pointer',
                    px: '16px', py: '8px',
                    '&:hover': { background: GREEN_HOVER },
                  }}
                >
                  Go to Materials Shortlist →
                </Box>
              )}
            </Box>
          )}

          {/* Materials, grouped by type */}
          {groups.map((group) => (
            <Box key={group.label} component="section">
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: '10px',
                px: '20px', py: '8px',
                background: '#f7f7f5',
              }}>
                <Typography component="h2" sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, whiteSpace: 'nowrap' }}>
                  {group.label} — {group.items.length} sample{group.items.length !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ flex: 1, height: '0.5px', background: BORDER }} />
              </Box>

              {group.items.map((item) => (
                <MaterialRow
                  key={item.id} item={item}
                  onRemove={submitted ? undefined : handleRemove}
                  scenesData={scenesData}
                  onOpenSource={submitted ? undefined : sourcePeek.openPeek}
                  isGlowing={sourcePeek.glowItemId === item.id}
                />
              ))}
            </Box>
          ))}

          {samplesItems.length > 0 && !submitted && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              mx: '20px', my: '16px', px: '10px', py: '7px',
              background: 'rgba(33,131,51,0.04)',
              borderLeft: '2px solid rgba(33,131,51,0.35)',
            }}>
              <Typography sx={{ fontSize: '10px', color: MUTED, lineHeight: 1.55, ...bodyFont }}>
                To add items, go back to Materials Shortlist.
                {' '}Removing an item here only removes it from this request.
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Right column: address form ── */}
        <Box sx={{ px: '20px', py: '14px' }}>

          <Typography component="h2" sx={{ ...labelMono, fontSize: '9px', color: MUTED, mb: '16px' }}>
            Delivery address
          </Typography>

          {submitted ? (
            /* ── Success state ── */
            <Box role="status" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
              <Box aria-hidden sx={{
                width: 36, height: 36,
                border: `1px solid ${GREEN}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </Box>

              <Typography sx={{
                fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
                fontWeight: 300, lineHeight: 1.1, letterSpacing: '-0.025em',
                color: CHARCOAL, ...bodyFont,
              }}>
                Request submitted.{' '}
                <Box component="span" sx={{ color: GREEN, fontStyle: 'italic' }}>
                  Samples on their way.
                </Box>
              </Typography>

              <Typography sx={{ fontSize: '0.875rem', color: MUTED, lineHeight: 1.7, ...bodyFont }}>
                We'll deliver your {samplesItems.length} sample{samplesItems.length !== 1 ? 's' : ''} within
                5–7 working days and follow up via WhatsApp.
              </Typography>
            </Box>
          ) : (
            /* ── Form ── */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {textFields.map(({ field, label, placeholder, autoComplete, inputMode }) => (
                <FormField key={field} label={label} htmlFor={`samples-${field}`}>
                  <Box
                    component="input"
                    id={`samples-${field}`}
                    type="text"
                    autoComplete={autoComplete}
                    inputMode={inputMode}
                    placeholder={placeholder}
                    value={address[field]}
                    onChange={handleChange(field)}
                    onFocus={() => setFocusedField(field)}
                    onBlur={() => setFocusedField(null)}
                    sx={inputSx(focusedField === field)}
                  />
                </FormField>
              ))}

              <FormField label="COUNTRY">
                {/* Pre-filled, not editable */}
                <Box sx={{
                  ...inputSx(false),
                  backgroundColor: STONE50,
                  borderColor: BORDER,
                  color: MUTED,
                  userSelect: 'none',
                  display: 'flex', alignItems: 'center',
                }}>
                  Singapore
                </Box>
              </FormField>

              {submitError && <InlineAlert severity="error">{submitError}</InlineAlert>}

              <Box sx={{ pt: 1, borderTop: `0.5px solid ${BORDER}`, mt: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || samplesItems.length === 0}
                  sx={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: (submitting || samplesItems.length === 0)
                      ? 'rgba(33,131,51,0.50)' : GREEN,
                    color: '#fff', border: 'none',
                    cursor: (submitting || samplesItems.length === 0)
                      ? 'not-allowed' : 'pointer',
                    px: 4, py: '14px',
                    fontSize: '0.875rem', fontWeight: 500, letterSpacing: '0.05em',
                    ...bodyFont,
                    transition: 'background-color 0.2s ease',
                    '&:hover': !submitting && samplesItems.length > 0 && {
                      backgroundColor: GREEN_HOVER,
                    },
                  }}
                >
                  {submitting ? 'Submitting…' : 'Confirm and request samples'}
                </Box>

                <Typography sx={{
                  ...labelMono, fontSize: '8px', color: MUTED_FAINT,
                  textAlign: 'center', mt: 1,
                }}>
                  {samplesItems.length === 0
                    ? 'Add at least one sample to continue.'
                    : 'This page locks after submission.'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <SourcePhotoPeek
        peek={sourcePeek.peek}
        shortlistName="Materials"
        returnLabel="Sample Delivery"
        onClose={sourcePeek.closePeek}
        onClosedWithChanges={sourcePeek.handleClosedWithChanges}
      />
    </Box>
  );
}
